import React, { useState, useRef, useEffect } from 'react';
import { Camera, Upload, X, Tag, MapPin, FileText, Plus, Loader, Info, Link, Cloud, Clock, Navigation } from 'lucide-react';
import { uploadPhoto } from '../lib/photoStorage';
import { uploadFileToOneDrive } from '../lib/microsoftGraph';
import { isMicrosoftConnected } from '../lib/microsoftGraph';
import { PhotoUploadProgress, PhotoMetadata } from '../types/photo';
import { useLocalAuth } from '../hooks/useLocalAuth';
import { getBuilding } from '../lib/localDatabase';
import { getApartmentLabel } from '../utils/getApartmentLabel';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

interface PhotoUploaderProps {
  buildingId: string;
  apartmentId: string;
  onUploadComplete: () => void;
  onCancel: () => void;
}

export const PhotoUploader: React.FC<PhotoUploaderProps> = ({
  buildingId,
  apartmentId,
  onUploadComplete,
  onCancel,
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [uploadProgress, setUploadProgress] = useState<PhotoUploadProgress[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [metadata, setMetadata] = useState<PhotoMetadata>({
    location: '',
    tags: [],
    notes: '',
    description: '',
    oneDriveUrl: '',
    gpsLocation: undefined,
    timestamp: new Date()
  });
  const [tagInput, setTagInput] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const { user } = useLocalAuth();
  const [buildingInfo, setBuildingInfo] = useState<{
    name: string;
    address: string;
    apartmentLabel?: string;
    apartmentNumber?: string;
    konzernId?: string;
  } | null>(null);
  const [autoFillEnabled, setAutoFillEnabled] = useState(true);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [useOneDrive, setUseOneDrive] = useState(false);
  const [oneDriveFolderPath, setOneDriveFolderPath] = useState('InstallTrack Pro/Photos');
  const [isMicrosoftAvailable, setIsMicrosoftAvailable] = useState(false);
  const [fileDescriptions, setFileDescriptions] = useState<Record<number, string>>({});
  const [gpsLocation, setGpsLocation] = useState<{
    latitude: number | null;
    longitude: number | null;
    address: string;
    isLoading: boolean;
    error: string | null;
  }>({
    latitude: null,
    longitude: null,
    address: '',
    isLoading: false,
    error: null
  });
  const [embedGpsData, setEmbedGpsData] = useState(true);
  const [embedTimestamp, setEmbedTimestamp] = useState(true);

  // Check if Microsoft is connected
  useEffect(() => {
    setIsMicrosoftAvailable(isMicrosoftConnected());
  }, []);

  // Get current GPS location
  useEffect(() => {
    if (embedGpsData) {
      setGpsLocation(prev => ({ ...prev, isLoading: true, error: null }));
      
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude, longitude } = position.coords;
            setGpsLocation(prev => ({ 
              ...prev, 
              latitude, 
              longitude,
              isLoading: false 
            }));
            
            // Try to get address from coordinates
            try {
              const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
                { headers: { 'Accept-Language': 'de' } }
              );
              
              if (response.ok) {
                const data = await response.json();
                const address = data.display_name || '';
                setGpsLocation(prev => ({ ...prev, address }));
                
                // Update metadata with GPS location
                setMetadata(prev => ({
                  ...prev,
                  gpsLocation: {
                    latitude,
                    longitude,
                    address
                  }
                }));
              }
            } catch (error) {
              console.error('Error getting address:', error);
            }
          },
          (error) => {
            console.error('Error getting location:', error);
            setGpsLocation(prev => ({ 
              ...prev, 
              isLoading: false, 
              error: 'Standortdaten konnten nicht abgerufen werden. Bitte erteilen Sie die Berechtigung oder aktivieren Sie GPS.' 
            }));
          },
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
      } else {
        setGpsLocation(prev => ({ 
          ...prev, 
          isLoading: false, 
          error: 'Geolocation wird von Ihrem Browser nicht unterstützt.' 
        }));
      }
    }
  }, [embedGpsData]);

  // Lade Gebäudeinformationen und Wohnungsinformationen
  useEffect(() => {
    if (buildingId) {
      const building = getBuilding(buildingId);
      if (building) {
        let apartmentLabel = '';
        let apartmentNumber = '';
        let konzernId = '';
        
        // Wenn eine Wohnung ausgewählt ist, finde die Informationen
        if (apartmentId) {
          for (const floor of building.floors) {
            const apartment = floor.apartments.find(apt => apt.id === apartmentId);
            if (apartment) {
              apartmentLabel = getApartmentLabel(apartment);
              apartmentNumber = apartment.formattedNumber;
              konzernId = apartment.konzernId || '';
              break;
            }
          }
        }
        
        setBuildingInfo({
          name: building.name,
          address: building.address,
          apartmentLabel,
          apartmentNumber,
          konzernId
        });
        
        // Automatisch Metadaten ausfüllen
        if (autoFillEnabled) {
          const autoLocation = apartmentLabel && apartmentNumber 
            ? `${apartmentLabel} ${apartmentNumber}` 
            : building.name;
            
          const autoTags = [];
          if (building.name) autoTags.push(building.name);
          if (apartmentLabel) autoTags.push(apartmentLabel);
          if (konzernId) autoTags.push(konzernId);
          
          const autoNotes = `Aufnahme in ${building.name}${apartmentLabel && apartmentNumber ? `, ${apartmentLabel} ${apartmentNumber}` : ''}${konzernId ? `, Konzern ID: ${konzernId}` : ''}`;
          
          setMetadata({
            location: autoLocation,
            tags: autoTags,
            notes: autoNotes,
            description: '',
            oneDriveUrl: '',
            gpsLocation: gpsLocation.latitude && gpsLocation.longitude ? {
              latitude: gpsLocation.latitude,
              longitude: gpsLocation.longitude,
              address: gpsLocation.address
            } : undefined,
            timestamp: new Date()
          });
        }
      }
    }
  }, [buildingId, apartmentId, autoFillEnabled, gpsLocation.latitude, gpsLocation.longitude, gpsLocation.address]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      setFiles(prev => [...prev, ...newFiles]);
      
      // Create previews
      const newPreviews = newFiles.map(file => URL.createObjectURL(file));
      setPreviews(prev => [...prev, ...newPreviews]);
      
      // Initialize descriptions for new files
      const newDescriptions = { ...fileDescriptions };
      newFiles.forEach((_, index) => {
        const newIndex = files.length + index;
        newDescriptions[newIndex] = '';
      });
      setFileDescriptions(newDescriptions);
      
      // Reset file input
      if (e.target === fileInputRef.current) {
        fileInputRef.current.value = '';
      } else if (e.target === cameraInputRef.current) {
        cameraInputRef.current.value = '';
      }
      
      // Clear any previous camera errors
      setCameraError(null);
    }
  };

  const handleCameraCapture = () => {
    // Check if the device has camera support
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setCameraError('Ihr Gerät unterstützt keine Kamerafunktion oder der Zugriff wurde verweigert.');
      return;
    }
    
    // Check if we're on a mobile device
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // On mobile devices, we need to handle camera access differently
    if (isMobile) {
      // First check if we have camera permission
      navigator.mediaDevices.getUserMedia({ video: true })
        .then(() => {
          // Permission granted, open the camera input
          if (cameraInputRef.current) {
            cameraInputRef.current.click();
          }
        })
        .catch(error => {
          console.error('Camera access error:', error);
          setCameraError('Kamerazugriff verweigert. Bitte erteilen Sie die Berechtigung in Ihren Browsereinstellungen.');
        });
    } else {
      // On desktop, just open the file input with capture attribute
      if (cameraInputRef.current) {
        cameraInputRef.current.click();
      }
    }
  };

  const handleFileSelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleRemoveFile = (index: number) => {
    // Revoke object URL to avoid memory leaks
    URL.revokeObjectURL(previews[index]);
    
    // Remove file and preview
    setFiles(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
    
    // Remove description
    const newDescriptions = { ...fileDescriptions };
    delete newDescriptions[index];
    
    // Reindex the remaining descriptions
    const reindexedDescriptions: Record<number, string> = {};
    Object.keys(newDescriptions).forEach((key, i) => {
      const numKey = parseInt(key);
      if (numKey > index) {
        reindexedDescriptions[i] = newDescriptions[numKey];
      } else {
        reindexedDescriptions[i] = newDescriptions[numKey];
      }
    });
    
    setFileDescriptions(reindexedDescriptions);
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !metadata.tags?.includes(tagInput.trim())) {
      setMetadata(prev => ({
        ...prev,
        tags: [...(prev.tags || []), tagInput.trim()],
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setMetadata(prev => ({
      ...prev,
      tags: prev.tags?.filter(t => t !== tag),
    }));
  };

  const toggleAutoFill = () => {
    setAutoFillEnabled(!autoFillEnabled);
    
    // Wenn Auto-Fill aktiviert wird, Metadaten neu generieren
    if (!autoFillEnabled && buildingInfo) {
      const autoLocation = buildingInfo.apartmentLabel && buildingInfo.apartmentNumber 
        ? `${buildingInfo.apartmentLabel} ${buildingInfo.apartmentNumber}` 
        : buildingInfo.name;
        
      const autoTags = [];
      if (buildingInfo.name) autoTags.push(buildingInfo.name);
      if (buildingInfo.apartmentLabel) autoTags.push(buildingInfo.apartmentLabel);
      if (buildingInfo.konzernId) autoTags.push(buildingInfo.konzernId);
      
      const autoNotes = `Aufnahme in ${buildingInfo.name}${buildingInfo.apartmentLabel && buildingInfo.apartmentNumber ? `, ${buildingInfo.apartmentLabel} ${buildingInfo.apartmentNumber}` : ''}${buildingInfo.konzernId ? `, Konzern ID: ${buildingInfo.konzernId}` : ''}`;
      
      setMetadata({
        location: autoLocation,
        tags: autoTags,
        notes: autoNotes,
        description: '',
        oneDriveUrl: '',
        gpsLocation: gpsLocation.latitude && gpsLocation.longitude ? {
          latitude: gpsLocation.latitude,
          longitude: gpsLocation.longitude,
          address: gpsLocation.address
        } : undefined,
        timestamp: new Date()
      });
    }
  };

  const handleFileDescriptionChange = (index: number, description: string) => {
    setFileDescriptions(prev => ({
      ...prev,
      [index]: description
    }));
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    
    setIsUploading(true);
    setUploadProgress(files.map(file => ({
      fileName: file.name,
      progress: 0,
      status: 'uploading',
    })));
    
    try {
      // Upload each file
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // Get the description for this file
        const fileDescription = fileDescriptions[i] || '';
        
        // Update progress for this file
        setUploadProgress(prev => prev.map((p, idx) => 
          idx === i ? { ...p, status: 'uploading' } : p
        ));
        
        try {
          // If OneDrive is enabled, upload to OneDrive first
          let oneDriveUrl = '';
          if (useOneDrive && isMicrosoftAvailable) {
            try {
              // Create folder path with building and apartment info
              let folderPath = oneDriveFolderPath;
              if (buildingInfo?.name) {
                folderPath += `/${buildingInfo.name}`;
                if (buildingInfo.apartmentLabel && buildingInfo.apartmentNumber) {
                  folderPath += `/${buildingInfo.apartmentLabel} ${buildingInfo.apartmentNumber}`;
                }
              }
              
              oneDriveUrl = await uploadFileToOneDrive(
                file,
                folderPath,
                (progress) => {
                  setUploadProgress(prev => prev.map((p, idx) => 
                    idx === i ? { ...p, progress: progress / 2 } : p
                  ));
                }
              );
            } catch (error) {
              console.error('Error uploading to OneDrive:', error);
              // Continue with local upload even if OneDrive fails
            }
          }
          
          // Prepare photo metadata with GPS and timestamp
          const photoMetadata = {
            ...metadata,
            description: fileDescription,
            oneDriveUrl,
            gpsLocation: embedGpsData && gpsLocation.latitude && gpsLocation.longitude ? {
              latitude: gpsLocation.latitude,
              longitude: gpsLocation.longitude,
              address: gpsLocation.address
            } : undefined,
            timestamp: embedTimestamp ? new Date() : undefined
          };
          
          // Then upload to local storage
          await uploadPhoto(
            file,
            buildingId,
            apartmentId,
            photoMetadata,
            (progress) => {
              // If we uploaded to OneDrive, start from 50%
              const adjustedProgress = useOneDrive && isMicrosoftAvailable
                ? 50 + (progress / 2)
                : progress;
                
              setUploadProgress(prev => prev.map((p, idx) => 
                idx === i ? { ...p, progress: adjustedProgress } : p
              ));
            }
          );
          
          // Mark as complete
          setUploadProgress(prev => prev.map((p, idx) => 
            idx === i ? { ...p, status: 'complete', progress: 100 } : p
          ));
        } catch (error) {
          console.error(`Error uploading file ${file.name}:`, error);
          
          // Mark as error
          setUploadProgress(prev => prev.map((p, idx) => 
            idx === i ? { 
              ...p, 
              status: 'error', 
              error: error instanceof Error ? error.message : 'Upload failed' 
            } : p
          ));
        }
      }
      
      // Check if all uploads were successful
      const allComplete = uploadProgress.every(p => p.status === 'complete');
      
      if (allComplete) {
        onUploadComplete();
      }
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 max-w-2xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-800">Fotos hinzufügen</h2>
        <button 
          onClick={onCancel}
          className="text-gray-500 hover:text-gray-700 transition-colors"
          disabled={isUploading}
        >
          <X className="w-6 h-6" />
        </button>
      </div>
      
      {/* File input (hidden) */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*"
        multiple
        className="hidden"
      />
      
      {/* Camera input (hidden) */}
      <input
        type="file"
        ref={cameraInputRef}
        onChange={handleFileChange}
        accept="image/*"
        capture="environment"
        className="hidden"
      />
      
      {/* Photo capture buttons */}
      <div className="flex gap-4 mb-6">
        <button
          onClick={handleCameraCapture}
          className="flex-1 flex items-center justify-center gap-2 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300"
          disabled={isUploading}
        >
          <Camera className="w-5 h-5" />
          <span>Kamera öffnen</span>
        </button>
        
        <button
          onClick={handleFileSelect}
          className="flex-1 flex items-center justify-center gap-2 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:bg-gray-300"
          disabled={isUploading}
        >
          <Upload className="w-5 h-5" />
          <span>Fotos auswählen</span>
        </button>
      </div>
      
      {/* Camera error message */}
      {cameraError && (
        <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg">
          <p className="flex items-center gap-2">
            <Info className="w-5 h-5 shrink-0" />
            <span>{cameraError}</span>
          </p>
          <p className="mt-2 text-sm">
            Tipp: Stellen Sie sicher, dass Sie die Kameraberechtigung erteilt haben und versuchen Sie es erneut. 
            Alternativ können Sie auch Fotos aus Ihrer Galerie auswählen.
          </p>
        </div>
      )}
      
      {/* GPS and timestamp options */}
      <div className="mb-6 p-4 bg-blue-50 rounded-lg">
        <h3 className="text-md font-semibold text-blue-800 mb-3">Metadaten-Einstellungen</h3>
        
        <div className="space-y-4">
          {/* GPS data toggle */}
          <div className="flex items-center justify-between">
            <div className="flex items-start gap-2">
              <Navigation className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-800">GPS-Daten einbetten</p>
                <p className="text-xs text-blue-700">Standortdaten werden in den Fotos gespeichert</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                className="sr-only peer" 
                checked={embedGpsData}
                onChange={() => setEmbedGpsData(!embedGpsData)}
                disabled={isUploading}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
          
          {/* Show GPS data if enabled */}
          {embedGpsData && (
            <div className="ml-7 p-3 bg-blue-100 rounded-lg">
              {gpsLocation.isLoading ? (
                <div className="flex items-center gap-2">
                  <Loader className="w-4 h-4 text-blue-600 animate-spin" />
                  <span className="text-sm text-blue-700">GPS-Daten werden abgerufen...</span>
                </div>
              ) : gpsLocation.error ? (
                <div className="text-sm text-red-600">{gpsLocation.error}</div>
              ) : gpsLocation.latitude && gpsLocation.longitude ? (
                <div className="space-y-1">
                  <p className="text-sm text-blue-700">
                    <span className="font-medium">Koordinaten:</span> {gpsLocation.latitude.toFixed(6)}, {gpsLocation.longitude.toFixed(6)}
                  </p>
                  {gpsLocation.address && (
                    <p className="text-sm text-blue-700">
                      <span className="font-medium">Adresse:</span> {gpsLocation.address}
                    </p>
                  )}
                </div>
              ) : (
                <div className="text-sm text-blue-700">Keine GPS-Daten verfügbar</div>
              )}
            </div>
          )}
          
          {/* Timestamp toggle */}
          <div className="flex items-center justify-between">
            <div className="flex items-start gap-2">
              <Clock className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-800">Zeitstempel einbetten</p>
                <p className="text-xs text-blue-700">Datum und Uhrzeit werden in den Fotos gespeichert</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                className="sr-only peer" 
                checked={embedTimestamp}
                onChange={() => setEmbedTimestamp(!embedTimestamp)}
                disabled={isUploading}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
          
          {/* Show current timestamp if enabled */}
          {embedTimestamp && (
            <div className="ml-7 p-3 bg-blue-100 rounded-lg">
              <p className="text-sm text-blue-700">
                <span className="font-medium">Aktuelles Datum/Uhrzeit:</span> {format(new Date(), 'dd.MM.yyyy HH:mm:ss', { locale: de })}
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Microsoft OneDrive integration */}
      {isMicrosoftAvailable && (
        <div className="mb-6 p-4 bg-blue-50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-medium text-blue-800 flex items-center gap-2">
              <Cloud className="w-5 h-5 text-blue-600" />
              <span>Microsoft OneDrive</span>
            </h3>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                className="sr-only peer" 
                checked={useOneDrive}
                onChange={() => setUseOneDrive(!useOneDrive)}
                disabled={isUploading}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
          
          {useOneDrive && (
            <div className="mt-2">
              <label className="block text-sm font-medium text-blue-700 mb-1">
                OneDrive Ordnerpfad
              </label>
              <input
                type="text"
                value={oneDriveFolderPath}
                onChange={(e) => setOneDriveFolderPath(e.target.value)}
                className="w-full p-2.5 border border-blue-300 rounded-lg bg-blue-50 focus:ring-blue-500 focus:border-blue-500"
                placeholder="z.B. InstallTrack Pro/Photos"
                disabled={isUploading}
              />
              <p className="mt-1 text-xs text-blue-600">
                Die Fotos werden in diesem Ordner in Ihrem OneDrive gespeichert. Der Gebäudename und die Wohnungsnummer werden automatisch als Unterordner hinzugefügt.
              </p>
            </div>
          )}
        </div>
      )}
      
      {/* Preview area */}
      {previews.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-700 mb-3">Vorschau</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {previews.map((preview, index) => (
              <div key={index} className="relative group border border-gray-200 rounded-lg p-2">
                <img 
                  src={preview} 
                  alt={`Preview ${index + 1}`} 
                  className="w-full h-32 object-cover rounded-lg mb-2"
                />
                <button
                  onClick={() => handleRemoveFile(index)}
                  className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  disabled={isUploading}
                >
                  <X className="w-4 h-4" />
                </button>
                
                {/* File description input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Beschreibung
                  </label>
                  <textarea
                    value={fileDescriptions[index] || ''}
                    onChange={(e) => handleFileDescriptionChange(index, e.target.value)}
                    placeholder="Beschreiben Sie dieses Foto..."
                    className="w-full p-2 border border-gray-300 rounded-lg text-sm resize-none h-20"
                    disabled={isUploading}
                  />
                </div>
                
                {uploadProgress[index] && uploadProgress[index].status !== 'complete' && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
                    {uploadProgress[index].status === 'uploading' ? (
                      <div className="text-white text-center">
                        <Loader className="w-8 h-8 animate-spin mx-auto mb-2" />
                        <span>{Math.round(uploadProgress[index].progress)}%</span>
                      </div>
                    ) : uploadProgress[index].status === 'error' ? (
                      <div className="text-red-300 text-center p-2">
                        <X className="w-8 h-8 mx-auto mb-1" />
                        <span className="text-xs">Fehler</span>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Auto-fill toggle */}
      <div className="flex items-center justify-between mb-4 bg-blue-50 p-3 rounded-lg">
        <div className="flex items-start gap-2">
          <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-800">Automatische Metadaten</p>
            <p className="text-xs text-blue-700">Metadaten werden automatisch aus Gebäude- und Wohnungsinformationen ausgefüllt</p>
          </div>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input 
            type="checkbox" 
            className="sr-only peer" 
            checked={autoFillEnabled}
            onChange={toggleAutoFill}
            disabled={isUploading}
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
        </label>
      </div>
      
      {/* Metadata form */}
      <div className="space-y-4 mb-6">
        <h3 className="text-lg font-semibold text-gray-700">Metadaten</h3>
        
        {/* Building info */}
        {buildingInfo && (
          <div className="bg-gray-50 p-3 rounded-lg mb-4">
            <p className="text-sm text-gray-700">
              <span className="font-medium">Gebäude:</span> {buildingInfo.name}
            </p>
            {buildingInfo.apartmentLabel && buildingInfo.apartmentNumber && (
              <p className="text-sm text-gray-700">
                <span className="font-medium">Wohneinheit:</span> {buildingInfo.apartmentLabel} {buildingInfo.apartmentNumber}
              </p>
            )}
            {buildingInfo.konzernId && (
              <p className="text-sm text-gray-700">
                <span className="font-medium">Konzern ID:</span> {buildingInfo.konzernId}
              </p>
            )}
          </div>
        )}
        
        {/* Location */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <div className="flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              <span>Ort/Raum</span>
            </div>
          </label>
          <input
            type="text"
            value={metadata.location || ''}
            onChange={(e) => setMetadata(prev => ({ ...prev, location: e.target.value }))}
            placeholder="z.B. Wohnzimmer, Küche, Bad"
            className="w-full p-2.5 border border-gray-300 rounded-lg"
            disabled={isUploading || autoFillEnabled}
          />
        </div>
        
        {/* Tags */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <div className="flex items-center gap-1">
              <Tag className="w-4 h-4" />
              <span>Tags</span>
            </div>
          </label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              placeholder="Tag hinzufügen"
              className="flex-1 p-2.5 border border-gray-300 rounded-lg"
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
              disabled={isUploading}
            />
            <button
              type="button"
              onClick={handleAddTag}
              className="p-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300"
              disabled={!tagInput.trim() || isUploading}
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
          {metadata.tags && metadata.tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {metadata.tags.map((tag, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-1 bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm"
                >
                  <span>{tag}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="text-blue-600 hover:text-blue-800"
                    disabled={isUploading || autoFillEnabled}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <div className="flex items-center gap-1">
              <FileText className="w-4 h-4" />
              <span>Notizen</span>
            </div>
          </label>
          <textarea
            value={metadata.notes || ''}
            onChange={(e) => setMetadata(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Zusätzliche Informationen zu den Fotos"
            className="w-full p-2.5 border border-gray-300 rounded-lg h-24 resize-none"
            disabled={isUploading || autoFillEnabled}
          />
        </div>
      </div>
      
      {/* User info */}
      <div className="bg-gray-50 p-3 rounded-lg mb-6">
        <p className="text-sm text-gray-600">
          <span className="font-medium">Aufgenommen von:</span> {user?.name || 'Unbekannt'}
          {user?.company && ` (${user.company})`}
        </p>
        <p className="text-sm text-gray-600">
          <span className="font-medium">Datum:</span> {format(new Date(), 'dd.MM.yyyy HH:mm', { locale: de })}
        </p>
        {embedGpsData && gpsLocation.latitude && gpsLocation.longitude && (
          <p className="text-sm text-gray-600">
            <span className="font-medium">Standort:</span> {gpsLocation.address || `${gpsLocation.latitude.toFixed(6)}, ${gpsLocation.longitude.toFixed(6)}`}
          </p>
        )}
      </div>
      
      {/* Action buttons */}
      <div className="flex justify-end gap-3">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          disabled={isUploading}
        >
          Abbrechen
        </button>
        <button
          onClick={handleUpload}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:bg-blue-300"
          disabled={files.length === 0 || isUploading}
        >
          {isUploading ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              <span>Wird hochgeladen...</span>
            </>
          ) : (
            <>
              <Upload className="w-5 h-5" />
              <span>Hochladen</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};